<template>
	<div id="app" class="game-screen paw-bg">
		<AppBackground v-if="
			hasSelectedCats
			&& 	((view !== 'EndScreen') || (view === 'EndScreen' && isTransitioning))" />
		<transition
			type="transition"
			:name="transitionName"
			:mode="transitionMode"
			@before-enter="isTransitioning = true"
			@after-enter="isTransitioning = false"
			>
			<component
				:is="view"
				v-if="configLoaded"
				:isTransitioning="isTransitioning"
				v-bind="viewSettings"
			/>
		</transition>
	</div>
</template>

<script>
import AssetLoader from '@/components/abstract/AssetLoader';
import AppBackground from '@/components/board/AppBackground';
import IntroScreen from '@/components/views/IntroScreen';
import CatSelector from '@/components/views/CatSelector';
import Tutorial from '@/components/views/Tutorial';
import StageIntro from '@/components/views/StageIntro';
import GameStage from '@/components/views/GameStage';
import GameOver from '@/components/views/GameOver';
import EndScreen from '@/components/views/EndScreen';
import soundFx from '@/mixins/sound-fx';
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';

export default {
	name: 'App',
	mixins: [soundFx],
	components: {
		AssetLoader,
		AppBackground,
		IntroScreen,
		CatSelector,
		Tutorial,
		StageIntro,
		GameStage,
		GameOver,
		EndScreen,
	},
	data() {
		return {
			isTransitioning: false,
		};
	},
	computed: {
		...mapGetters([
			"configLoaded",
			"hasSelectedCats",
			"currentStageSettings",
		]),
		...mapState({
			gameOver: state => state.gameOver,
			view: state => state.views.current.component,
			viewSettings: state => state.views.current.settings,
		}),
		transitionName() {
			let transitionName = "";

			switch (this.view) {
			case "AssetLoader":
			case "IntroScreen":
			case "StageIntro":
			case "GameStage":
				transitionName = "fade";
				break;
			case "GameOver":
				transitionName = "angel-wipe";
				break;
			default:
				transitionName = "fade";
				break;
			}

			return transitionName;
		},
		transitionMode() {
			let transitionMode = "";

			switch (this.view) {
			case "StageIntro":
				transitionMode = "out-in";
				break;
			default:
				transitionMode = "";
				break;
			}

			return transitionMode;
		},
	},
	methods: {
		...mapActions([
			"getGameConfig",
			"pauseAllSounds",
			"unpauseAllSounds",
			"sendEvent",
		]),
		...mapMutations([
			"setNowPlaying",
		]),
	},
	mounted() {
		this.getGameConfig();

		const unwatchLoader = this.$watch('configLoaded', (configLoaded) => {
			if (configLoaded) {
				const musicSrc = this.soundObjectToArray(this.currentStageSettings.music);

				const music = this.createHowl(musicSrc, {
					loop: true,
				});

				music.volume(0);
				music.play();
				music.fade(0, 1, 2000);

				this.setNowPlaying({
					music,
				});
				unwatchLoader();
			}
		});

		this.sendEvent({
			ec: "Game",
			ea: "Arrive",
			el: "Arrive",
		});

		document.addEventListener("visibilitychange", () => {
			if (document.hidden) {
				this.pauseAllSounds();
			} else {
				this.unpauseAllSounds();
			}
		});
	},
};
</script>
<style lang="scss">
	@import 'assets/styles/utilities/**/*.scss';

	html, body {
		height: 100%;
		overflow-y: hidden;
		-webkit-overflow-scrolling: touch;
		touch-action: manipulation;
	}

	#app {
		touch-action: manipulation;
		width: 100%;
		height: 100%;
		overflow: hidden;
		font-family: $body-font-stack;
		font-weight: 300;
		font-size: 20px;
		user-select: none;

		@include mobile {
			font-size: 14px;
		}
	}

	.game-screen {
		width: 100%;
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		color: #fff;
		position: absolute;
		top: 0;
		left: 0;
		overflow: hidden;
	}

	.paw-bg {
		animation: bg-crawl 60s infinite linear;
		background-size: 10%, 100%;
		background-image: url('~@assets/img/paw-pattern.svg'),
			linear-gradient(to right, $brand-color-primary, $brand-color-primary-light);
	}

	.content {
		width: 100%;
		text-align: center;
		padding: 1vh 1vw;
		max-width: 850px;
	}

	a {
		color: inherit;
		text-decoration: underline;
		transition: all $ui-transition-duration;

		&:hover {
			color: $brand-color-primary-dark;
		}
	}
</style>



// WEBPACK FOOTER //
// src/App.vue